// UI copy for Italian and English, plus the `t` lookup helper.
import { loadAccount } from "./data/account.js";
import { app } from "./lib/dom.js";
import { logout, render } from "./router.js";
import { state } from "./state.js";

export const copy = {
  en: {
    tagline: "A playful learning space where every child can grow at their own pace.",
    adaptive: "Exercises matched to each profile",
    safe: "Family and classroom connections",
    progress: "Private progress shared across web and iPhone",
    welcome: "Welcome back",
    loginHelp: "Use your email or username to enter your learning space.",
    login: "Log in",
    register: "Create account",
    identifier: "Email or username",
    password: "Password",
    forgot: "Forgot password?",
    email: "Email",
    username: "Username",
    birthDate: "Birth date",
    country: "Country code",
    role: "Account type",
    parent: "Parent",
    learner: "Learner",
    teacher: "Teacher",
    accept: "I accept the Privacy Policy and Terms of Service.",
    registrationSent: "Account created. Check your email to confirm it before signing in.",
    resetSent: "If this email has an account, a reset link has been sent.",
    profiles: "Who is learning?",
    profilesHelp: "Choose a profile to continue. Each profile has separate exercises and progress.",
    addProfile: "Add child profile",
    nickname: "Nickname",
    ageBand: "Age band",
    createProfile: "Create profile",
    home: "Home",
    exercises: "Exercises",
    progressNav: "Progress",
    account: "Account",
    greeting: "Hi",
    learningSpace: "Here is your learning space.",
    pendingTitle: "Exercises to complete",
    noExercises: "No pending exercises",
    noExercisesHelp: "All available exercises for this age have been completed. New exercises will appear here when assigned.",
    questions: "questions",
    level: "Level",
    xp: "XP",
    solved: "Questions solved",
    profileProgress: "Your progress",
    profileProgressHelp: "Progress belongs only to this profile and is synchronized with the iPhone app.",
    selectProfile: "Change profile",
    logout: "Log out",
    question: "Question",
    hint: "Show hint",
    next: "Next question",
    summary: "Exercise complete!",
    correct: "Correct",
    score: "Final score",
    closeHome: "Close and return Home",
    nice: "Nice reasoning!",
    tryAgain: "Good try — learn from the explanation.",
    install: "Install BrainMath",
    accountTitle: "Account and profiles",
    webReady: "Your BrainMath account works on both the website and iPhone.",
    loading: "Loading…"
  },
  it: {
    tagline: "Uno spazio di apprendimento giocoso dove ogni bambino cresce con i propri tempi.",
    adaptive: "Esercizi adatti a ogni profilo",
    safe: "Collegamenti sicuri con famiglia e classe",
    progress: "Progressi privati condivisi tra web e iPhone",
    welcome: "Bentornato",
    loginHelp: "Usa email o username per entrare nel tuo spazio di apprendimento.",
    login: "Accedi",
    register: "Crea account",
    identifier: "Email o username",
    password: "Password",
    forgot: "Password dimenticata?",
    email: "Email",
    username: "Username",
    birthDate: "Data di nascita",
    country: "Codice paese",
    role: "Tipo di account",
    parent: "Genitore",
    learner: "Studente",
    teacher: "Insegnante",
    accept: "Accetto Informativa Privacy e Termini di Servizio.",
    registrationSent: "Account creato. Controlla l'email e confermalo prima di accedere.",
    resetSent: "Se esiste un account per questa email, è stato inviato un link di recupero.",
    profiles: "Chi sta imparando?",
    profilesHelp: "Scegli un profilo. Ogni profilo ha esercizi e progressi separati.",
    addProfile: "Aggiungi profilo bambino",
    nickname: "Nickname",
    ageBand: "Fascia d'età",
    createProfile: "Crea profilo",
    home: "Home",
    exercises: "Esercizi",
    progressNav: "Progressi",
    account: "Account",
    greeting: "Ciao",
    learningSpace: "Questo è il tuo spazio di apprendimento.",
    pendingTitle: "Esercizi da completare",
    noExercises: "Nessun esercizio in sospeso",
    noExercisesHelp: "Tutti gli esercizi disponibili per questa età sono stati completati. I nuovi esercizi appariranno qui quando assegnati.",
    questions: "domande",
    level: "Livello",
    xp: "XP",
    solved: "Domande completate",
    profileProgress: "I tuoi progressi",
    profileProgressHelp: "I progressi appartengono solo a questo profilo e sono sincronizzati con l'app iPhone.",
    selectProfile: "Cambia profilo",
    logout: "Esci",
    question: "Domanda",
    hint: "Mostra suggerimento",
    next: "Prossima domanda",
    summary: "Esercizio completato!",
    correct: "Corrette",
    score: "Punteggio finale",
    closeHome: "Chiudi e torna alla Home",
    nice: "Ottimo ragionamento!",
    tryAgain: "Bel tentativo — impariamo dalla spiegazione.",
    install: "Installa BrainMath",
    accountTitle: "Account e profili",
    webReady: "Il tuo account BrainMath funziona sia sul sito sia su iPhone.",
    loading: "Caricamento…"
  }
};

export const t = key => copy[state.language][key] || copy.en[key] || key;

export function languageControl() {
  return `<div class="language-switcher" role="group" aria-label="${state.language === "it" ? "Scegli lingua" : "Choose language"}">
    <button type="button" class="language-choice ${state.language === "it" ? "active" : ""}" data-language-choice="it" aria-pressed="${state.language === "it"}"><span>🇮🇹</span><strong>Italiano</strong><i>IT</i></button>
    <button type="button" class="language-choice ${state.language === "en" ? "active" : ""}" data-language-choice="en" aria-pressed="${state.language === "en"}"><span>🇬🇧</span><strong>English</strong><i>EN</i></button>
  </div>`;
}

export function bindLanguage() {
  document.querySelectorAll("[data-language-choice]").forEach(button => {
    button.addEventListener("click", async () => {
      const selectedLanguage = button.dataset.languageChoice;
      if (!selectedLanguage || selectedLanguage === state.language) return;
      state.language = selectedLanguage;
      localStorage.setItem("brainmath-language", state.language);
      document.documentElement.lang = state.language;
      if (state.session) await loadAccount();
      render();
    });
  });
}
